import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { availabilityObjToArray, formatTimeSlot } from '../utils/dateUtils';
import { api } from '../services/api';
import Input from '../components/ui/Input';
import ProgressBar from '../components/ProgressBar';
import Button from '../components/ui/Button';
import HelpButton from '../components/HelpButton';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Clock, Edit, BookOpen, Target, AlertCircle, Mail, User } from 'lucide-react';

/**
 * Registration page for users coming from /start (with progress bar, no header)
 */
const StartRegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get availability data from location state
  const { returnState } = location.state || {};
  const { availability, isTutoringTrial, urlParams, ibResponse } = returnState || {};

  // Goal data state - Initialize from URL params OR IB response if available
  const [goalData, setGoalData] = useState(() => {
    const initialData = {
      goal: '',
      goal_level: '',
      exam_type: '',
      exam_level: '',
      exam_year_phase: '',
      cantonese_level: ''
    };
    
    // If IB response exists, set it as the goal
    if (ibResponse) {
      initialData.goal = ibResponse;
      initialData.exam_type = 'IB';
      return initialData;
    }
    
    // Pre-fill from URL parameters if they exist
    if (urlParams) {
      if (urlParams.exam) {
        initialData.goal = 'exam';
        initialData.exam_type = urlParams.exam; // e.g., 'IB', 'IGCSE 0523', etc.
      } else if (urlParams.goal) {
        initialData.goal = urlParams.goal; // e.g., 'hsk', 'conversational', etc.
      }
      
      if (urlParams.level) {
        initialData.goal_level = urlParams.level; // e.g., '4', 'Beginner', etc.
      }
    }
    
    return initialData;
  });
    
  // State for class type
  const [classType, setClassType] = useState(null);
  const [loadingClassType, setLoadingClassType] = useState(false);
  
  // Load class type data
  useEffect(() => {
    const loadClassType = async () => {
      const classTypeId = sessionStorage.getItem('startClassTypeId');
      if (!classTypeId) return;
      
      try {
        setLoadingClassType(true);
        const types = await api.classTypes.getActive();
        const selectedType = types.find(type => type.$id === classTypeId);
        if (selectedType) {
          setClassType(selectedType);
        }
      } catch (err) {
        console.error('Error loading class type:', err);
      } finally {
        setLoadingClassType(false);
      }
    };
    
    loadClassType();
  }, []);
  
  // Handle back to edit availability
  const handleEditAvailability = () => {
    const classTypeId = sessionStorage.getItem('startClassTypeId');
    
    if (classTypeId) {
      navigate(`/start/${classTypeId}`, {
        state: { availability, ibResponse }
      });
    } else {
      navigate('/start');
    }
  };
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Validate form fields
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone number is optional, but validate format if provided
    if (formData.phone.trim()) {
      // Normalize phone number (remove spaces, dashes, parentheses)
      const normalizedPhone = formData.phone.replace(/[\s\-\(\)]/g, '');

      // Validate phone format
      if (!normalizedPhone.startsWith('+')) {
        errors.phone = 'Phone number must start with a country code (e.g., +852)';
      } else if (normalizedPhone.length < 8 || normalizedPhone.length > 16) {
        errors.phone = 'Phone number must be between 8-15 digits (including country code)';
      } else if (!/^\+\d+$/.test(normalizedPhone)) {
        errors.phone = 'Phone number can only contain numbers after the + sign';
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission - OPTIMIZED VERSION
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // ADD GOAL VALIDATION HERE:
    // Validate goal selection based on class type (only if no URL params AND no IB response)
    if (!urlParams && !ibResponse) {
      if (!isTutoringTrial) {
        if (classType?.category === 'mandarin' && !goalData.goal) {
          setError('Please select your learning goal');
          return;
        }
        
        if (classType?.category === 'cantonese' && !goalData.cantonese_level) {
          setError('Please select your Cantonese level');
          return;
        }
      } else {
        // For tutoring trials, require a goal selection
        if (!goalData.goal) {
          setError('Please select your learning goal');
          return;
        }
      }
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Normalize phone number for Appwrite (remove spaces, dashes, etc.)
      // If phone is empty, pass empty string
      const normalizedPhone = formData.phone && formData.phone.trim().length > 0
        ? formData.phone.replace(/[\s\-\(\)]/g, '')
        : undefined;

      // ADD THESE DEBUG LINES:
      console.log('=== DEBUG: Phone normalization ===');
      console.log('formData.phone:', formData.phone);
      console.log('formData.phone.trim():', formData.phone.trim());
      console.log('normalizedPhone:', normalizedPhone);
      console.log('normalizedPhone type:', typeof normalizedPhone);
      console.log('normalizedPhone === undefined:', normalizedPhone === undefined);
      console.log('normalizedPhone === "":', normalizedPhone === '');
      console.log('=== END DEBUG ===');

      // Step 1: Register user (this creates account + session)
      console.log('Starting user registration...');
      const newUser = await register(
        formData.email,
        formData.name,
        normalizedPhone
      );
      
      console.log('Registration complete, user ID:', newUser.$id);
      
      // Step 2: Prepare data for availability submission
      const availabilityArray = availabilityObjToArray(availability);
      const classTypeId = sessionStorage.getItem('startClassTypeId');
      const classTypeCategory = classType?.category || classType?.name?.toLowerCase() || 'general';
      
      console.log('Submitting availability for user:', newUser.$id);
      
      // Step 3: Submit availability (non-blocking - fire and continue)
      const availabilityPromise = api.availability.submit(
        newUser.$id,
        classTypeId,
        availabilityArray,
        formData.name,
        formData.email,
        normalizedPhone,
        goalData,
        isTutoringTrial
      );
      
      // Step 4: Send email confirmation (non-blocking - fire and continue)
      const emailPromise = api.notifications.sendAvailabilityConfirmation(
        formData.name,
        formData.email,
        classTypeCategory,
        availabilityArray,
        classTypeId,
        normalizedPhone,
        goalData,
        isTutoringTrial
      ).catch(emailError => {
        console.warn('Failed to send confirmation email:', emailError);
        // Don't fail the whole process if email fails
      });
      
      // Step 5: Navigate immediately to success page (don't wait for background tasks)
      console.log('Navigating to success page...');
      navigate('/start-success', {
        state: {
          classType: classTypeCategory,
          availability,
          userEmail: formData.email,
          goalData,
          isTutoringTrial
        }
      });
      
      // Background tasks continue (availability submission and email)
      Promise.all([availabilityPromise, emailPromise]).then(() => {
        console.log('Background tasks completed successfully');
      }).catch(err => {
        console.warn('Some background tasks failed:', err);
        // User is already on success page, so this doesn't affect UX
      });
      
    } catch (err) {
      console.error('Registration/submission error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check if this is an IB Chinese class
  const isIBChinese = sessionStorage.getItem('startClassTypeId') === 'ib-chinese-classes';
  
  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      {/* Main content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-md w-full mx-auto">
          {/* Progress bar */}
          <ProgressBar currentStep={2} />
          
          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">Tell us about yourself</h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>
          
          {/* Show class type info */}
          {classType && (
            <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-4 py-3 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base flex items-center">
                    <BookOpen size={16} className="mr-2" />
                    Your Selected Class
                  </h3>
                </div>
              </div>
              <div className="p-4">
                <p className="text-gray-300 text-sm">{classType.name}</p>
                
                {/* Show IB response if present */}
                {ibResponse && (
                  <div className="pt-3 mt-3 border-t border-gray-700">
                    <div className="flex items-start">
                      <Target size={14} className="mr-2 mt-0.5 text-purple-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Programme Details</p>
                        <p className="text-sm text-white">{ibResponse}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Show goal information if URL params are present */}
                {!ibResponse && urlParams && (urlParams.goal || urlParams.exam) && (
                  <div className="pt-3 mt-3 border-t border-gray-700">
                    <div className="flex items-start">
                      <Target size={14} className="mr-2 mt-0.5 text-purple-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400 mb-1">What you'll be learning</p>
                        <p className="text-sm font-medium text-white">
                          {(() => {
                            // Display goal based on URL params
                            if (urlParams.exam) {
                              return 'School Exam Support';
                            } else if (urlParams.goal === 'hsk') {
                              return 'HSK Test Preparation';
                            } else if (urlParams.goal === 'hskk') {
                              return 'HSKK Speaking Test';
                            } else if (urlParams.goal === 'business') {
                              return 'Business Mandarin';
                            } else if (urlParams.goal === 'conversational') {
                              return 'Light Conversational (no homework)';
                            }
                            return null;
                          })()}
                        </p>
                        
                        {/* Show level/details */}
                        {(() => {
                          // For HSK
                          if (urlParams.goal === 'hsk' && urlParams.level) {
                            return <p className="text-xs text-gray-400 mt-1">Level {urlParams.level}</p>;
                          }
                          
                          // For HSKK
                          if (urlParams.goal === 'hskk' && urlParams.level) {
                            return <p className="text-xs text-gray-400 mt-1">{urlParams.level} Level</p>;
                          }
                          
                          // For Conversational
                          if (urlParams.goal === 'conversational' && urlParams.level) {
                            return <p className="text-xs text-gray-400 mt-1">{urlParams.level} Level</p>;
                          }
                          
                          // For Exams
                          if (urlParams.exam) {
                            const examDetails = [];
                            
                            // Add exam type
                            examDetails.push(urlParams.exam);
                            
                            // Add level if IB
                            if (urlParams.exam === 'IB' && urlParams.level) {
                              examDetails.push(urlParams.level);
                            }
                            
                            // Add phase/year
                            if (urlParams.phase) {
                              examDetails.push(urlParams.phase);
                            }
                            
                            return <p className="text-xs text-gray-400 mt-1">{examDetails.join(' - ')}</p>;
                          }
                          
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Show selected times */}
          {availability && availabilityObjToArray(availability).length > 0 && (
            <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-4 py-3 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base flex items-center">
                    <Clock size={16} className="mr-2" />
                    Your Selected Times
                  </h3>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={handleEditAvailability}
                    className="text-purple-400 hover:text-purple-300 flex items-center text-sm"
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="space-y-4">
                  {(() => {
                    // Parse and sort slots
                    const slots = availabilityObjToArray(availability);
                    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                    
                    // Parse slot strings into objects
                    const parsedSlots = slots.map(slotStr => {
                      const [day, time] = slotStr.split('-');
                      return { day: day.trim(), time: time.trim(), original: slotStr };
                    });
                    
                    // Group by day
                    const slotsByDay = parsedSlots.reduce((acc, slot) => {
                      if (!acc[slot.day]) {
                        acc[slot.day] = [];
                      }
                      acc[slot.day].push(slot);
                      return acc;
                    }, {});
                    
                    // Sort each day's slots by time
                    Object.keys(slotsByDay).forEach(day => {
                      slotsByDay[day].sort((a, b) => {
                        // Convert to 24-hour format for comparison
                        const convertTo24Hour = (timeStr) => {
                          const [time, period] = timeStr.split(' ');
                          let [hours, minutes] = time.split(':').map(Number);
                          
                          if (period === 'PM' && hours !== 12) {
                            hours += 12;
                          } else if (period === 'AM' && hours === 12) {
                            hours = 0;
                          }
                          
                          return hours * 60 + minutes;
                        };
                        
                        return convertTo24Hour(a.time) - convertTo24Hour(b.time);
                      });
                    });
                    
                    // Helper function to format time range
                    const formatTimeRange = (timeStr) => {
                      const [time, period] = timeStr.split(' ');
                      let [hours, minutes] = time.split(':').map(Number);
                      
                      // Convert to 24-hour for calculation
                      let hours24 = hours;
                      if (period === 'PM' && hours !== 12) {
                        hours24 += 12;
                      } else if (period === 'AM' && hours === 12) {
                        hours24 = 0;
                      }
                      
                      // Calculate end time (1 hour later)
                      let endMinutes = minutes + 30;
                      let endHours24 = hours24;

                      if (endMinutes >= 60) {
                        endMinutes -= 60;
                        endHours24 += 1;
                      }

                      let endHours = endHours24 % 12 || 12;
                      let endPeriod = endHours24 >= 12 ? 'PM' : 'AM';

                      return `${timeStr} - ${endHours}:${endMinutes.toString().padStart(2, '0')} ${endPeriod}`;
                    };
                    
                    // Render by day in order
                    return dayOrder.map(day => {
                      if (!slotsByDay[day] || slotsByDay[day].length === 0) return null;
                      
                      return (
                        <div key={day}>
                          <h4 className="text-sm font-semibold text-white mb-2">{day}</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-2">
                            {slotsByDay[day].map((slot, index) => (
                              <div key={index} className="flex items-center text-sm">
                                <span className="text-purple-400 mr-2">•</span>
                                <span className="text-gray-300">{formatTimeRange(slot.time)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }).filter(Boolean);
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Goals Section - Only show for regular classes when no IB response and no URL params */}
          {!isTutoringTrial && !loadingClassType && !urlParams && !ibResponse && classType && (() => {
            // Check if it's a Mandarin class
            const isMandarin = classType.name?.toLowerCase() == 'mandarin';              
            
            // Check if it's a Cantonese class
            const isCantonese = classType.name?.toLowerCase() == 'cantonese';

            // Only render the card if it's Mandarin or Cantonese
            if (!isMandarin && !isCantonese) {
              return null;
            }

            return (
              <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-4 py-3 border-b border-gray-700">
                  <h3 className="font-semibold text-base flex items-center">
                    <Target size={16} className="mr-2" />
                    What's your goal?
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  {isMandarin && (
                    <>
                      {[
                        { value: 'hsk', label: 'HSK Test Preparation' },
                        { value: 'exam', label: 'School Exam Support' },
                        { value: 'conversational', label: 'Light Conversational (no homework)' }
                      ].map((option) => (
                        <div key={option.value}>
                          <label
                            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                              goalData.goal === option.value
                                ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                                : 'border-gray-600 hover:border-gray-500'
                            }`}
                          >
                            <input
                              type="radio"
                              name="goal"
                              value={option.value}
                              checked={goalData.goal === option.value}
                              onChange={(e) => setGoalData(prev => ({ 
                                ...prev, 
                                goal: e.target.value,
                                goal_level: '',
                                exam_type: '',
                                exam_level: '',
                                exam_year_phase: ''
                              }))}
                              className="sr-only"
                            />
                            <span className="flex-1 text-sm font-medium">{option.label}</span>
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              goalData.goal === option.value ? 'border-purple-500' : 'border-gray-500'
                            }`}>
                              {goalData.goal === option.value && (
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                              )}
                            </div>
                          </label>

                          {/* HSK Level Selection */}
                          {goalData.goal === 'hsk' && option.value === 'hsk' && (
                            <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                              <label className="block text-sm font-medium text-gray-300 mb-3">
                                Which HSK level are you targeting?
                              </label>
                              <div className="grid grid-cols-3 gap-2">
                                {[1, 2, 3, 4, 5, 6].map((level) => (
                                  <button
                                    key={level}
                                    type="button"
                                    onClick={() => setGoalData(prev => ({ ...prev, goal_level: level.toString() }))}
                                    className={`p-2 rounded-md text-sm font-medium transition-all ${
                                      goalData.goal_level === level.toString()
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                    }`}
                                  >
                                    HSK {level}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Conversational Level Selection */}
                          {goalData.goal === 'conversational' && option.value === 'conversational' && (
                            <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                              <label className="block text-sm font-medium text-gray-300 mb-3">
                                What level are you?
                              </label>
                              <div className="grid grid-cols-3 gap-2">
                                {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                                  <button
                                    key={level}
                                    type="button"
                                    onClick={() => setGoalData(prev => ({ ...prev, goal_level: level }))}
                                    className={`p-2 rounded-md text-sm font-medium transition-all ${
                                      goalData.goal_level === level
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                    }`}
                                  >
                                    {level}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Exam Details */}
                          {goalData.goal === 'exam' && option.value === 'exam' && (
                            <div className="mt-3 ml-4 space-y-4">
                              <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                                <label className="block text-sm font-medium text-gray-300 mb-3">
                                  Which exam are you preparing for?
                                </label>
                                <select
                                  value={goalData.exam_type}
                                  onChange={(e) => setGoalData(prev => ({
                                    ...prev,
                                    exam_type: e.target.value,
                                    exam_level: '',
                                    exam_year_phase: ''
                                  }))}
                                  className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-white text-sm"
                                >
                                  <option value="">Select exam...</option>
                                  {['IGCSE 0523', 'IGCSE 0547', 'IB', 'A-Level', 'DSE'].map((exam) => (
                                    <option key={exam} value={exam}>{exam}</option>
                                  ))}
                                </select>
                              </div>

                              {goalData.exam_type === 'IB' && (
                                <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                                  <label className="block text-sm font-medium text-gray-300 mb-3">
                                    IB Level
                                  </label>
                                  <div className="grid grid-cols-2 gap-2">
                                    {['IB SL', 'IB HL'].map((level) => (
                                      <button
                                        key={level}
                                        type="button"
                                        onClick={() => setGoalData(prev => ({ ...prev, exam_level: level }))}
                                        className={`p-2 rounded-md text-sm font-medium transition-all ${
                                          goalData.exam_level === level
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                        }`}
                                      >
                                        {level}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {goalData.exam_type && (goalData.exam_type !== 'IB' || goalData.exam_level) && (
                                <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                                  <label className="block text-sm font-medium text-gray-300 mb-3">
                                    Year/Phase
                                  </label>
                                  <div className="grid grid-cols-2 gap-2">
                                    {(() => {
                                      if (goalData.exam_type.includes('IGCSE')) return ['Y10', 'Y11', 'Intensive'];
                                      if (goalData.exam_type === 'IB') return ['Year 1', 'Year 2', 'Intensive'];
                                      if (goalData.exam_type.includes('A-Level')) return ['AS', 'A2', 'Retake', 'Intensive'];
                                      if (goalData.exam_type.includes('DSE')) return ['F4', 'F5', 'F6', 'Intensive'];
                                      return [];
                                    })().map((phase) => (
                                      <button
                                        key={phase}
                                        type="button"
                                        onClick={() => setGoalData(prev => ({ ...prev, exam_year_phase: phase }))}
                                        className={`p-2 rounded-md text-sm font-medium transition-all ${
                                          goalData.exam_year_phase === phase
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                        }`}
                                      >
                                        {phase}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Business/HSKK Notice */}
                      <div className="mt-4 p-3 bg-blue-900 bg-opacity-20 border border-blue-700 rounded-lg">
                        <div className="flex items-start">
                          <AlertCircle size={16} className="text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="text-blue-200 text-xs">
                            <strong>Looking for Business Mandarin or HSKK?</strong> For specialized business Mandarin or HSKK speaking test prep, consider our{' '}
                            <a 
                              href="https://www.mandarintutorhk.com/services" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-300 hover:text-blue-200 underline"
                            >
                              private tutoring sessions
                            </a>
                            {' '}for more focused learning.
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {isCantonese && (
                    <>
                      <p className="text-sm text-gray-400 mb-3">What level are you?</p>
                      {[
                        { value: 'beginner', label: 'Beginner' },
                        { value: 'intermediate', label: 'Intermediate' },
                        { value: 'advanced', label: 'Advanced' }
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                            goalData.cantonese_level === option.value
                              ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                              : 'border-gray-600 hover:border-gray-500'
                          }`}
                        >
                          <input
                            type="radio"
                            name="cantoneseLevel"
                            value={option.value}
                            checked={goalData.cantonese_level === option.value}
                            onChange={(e) => setGoalData(prev => ({ ...prev, cantonese_level: e.target.value }))}
                            className="sr-only"
                          />
                          <span className="flex-1 text-sm font-medium">{option.label}</span>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            goalData.cantonese_level === option.value ? 'border-purple-500' : 'border-gray-500'
                          }`}>
                            {goalData.cantonese_level === option.value && (
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            )}
                          </div>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Goals Section - Show for tutoring trials when no URL params and no IB response */}
          {isTutoringTrial && !loadingClassType && !urlParams && !ibResponse && (
            <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-4 py-3 border-b border-gray-700">
                <h3 className="font-semibold text-base flex items-center">
                  <Target size={16} className="mr-2" />
                  What's your goal?
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {[
                  { value: 'hsk', label: 'HSK Test Preparation' },
                  { value: 'hskk', label: 'HSKK (Speaking Test)' },
                  { value: 'business', label: 'Business Mandarin' },
                  { value: 'exam', label: 'School Exam Support' },
                  { value: 'conversational', label: 'Light Conversational' }
                ].map((option) => (
                  <div key={option.value}>
                    <label
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                        goalData.goal === option.value
                          ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="goal"
                        value={option.value}
                        checked={goalData.goal === option.value}
                        onChange={(e) => setGoalData(prev => ({ 
                          ...prev, 
                          goal: e.target.value,
                          goal_level: '',
                          exam_type: '',
                          exam_level: '',
                          exam_year_phase: ''
                        }))}
                        className="sr-only"
                      />
                      <span className="flex-1 text-sm font-medium">{option.label}</span>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        goalData.goal === option.value ? 'border-purple-500' : 'border-gray-500'
                      }`}>
                        {goalData.goal === option.value && (
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        )}
                      </div>
                    </label>

                    {/* HSK Level Selection */}
                    {goalData.goal === 'hsk' && option.value === 'hsk' && (
                      <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          Which HSK level are you targeting?
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {[1, 2, 3, 4, 5, 6].map((level) => (
                            <button
                              key={level}
                              type="button"
                              onClick={() => setGoalData(prev => ({ ...prev, goal_level: level.toString() }))}
                              className={`p-2 rounded-md text-sm font-medium transition-all ${
                                goalData.goal_level === level.toString()
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              }`}
                            >
                              HSK {level}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* HSKK Level Selection */}
                    {goalData.goal === 'hskk' && option.value === 'hskk' && (
                      <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          Which HSKK level are you targeting?
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                            <button
                              key={level}
                              type="button"
                              onClick={() => setGoalData(prev => ({ ...prev, goal_level: level }))}
                              className={`p-2 rounded-md text-sm font-medium transition-all ${
                                goalData.goal_level === level
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              }`}
                            >
                              {level}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Conversational Level Selection */}
                    {goalData.goal === 'conversational' && option.value === 'conversational' && (
                      <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          What level are you?
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                            <button
                              key={level}
                              type="button"
                              onClick={() => setGoalData(prev => ({ ...prev, goal_level: level }))}
                              className={`p-2 rounded-md text-sm font-medium transition-all ${
                                goalData.goal_level === level
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              }`}
                            >
                              {level}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Exam Details */}
                    {goalData.goal === 'exam' && option.value === 'exam' && (
                      <div className="mt-3 ml-4 space-y-4">
                        <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                          <label className="block text-sm font-medium text-gray-300 mb-3">
                            Which exam are you preparing for?
                          </label>
                          <select
                            value={goalData.exam_type}
                            onChange={(e) => setGoalData(prev => ({
                              ...prev,
                              exam_type: e.target.value,
                              exam_level: '',
                              exam_year_phase: ''
                            }))}
                            className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-white text-sm"
                          >
                            <option value="">Select exam...</option>
                            {['IGCSE 0523', 'IGCSE 0547', 'IB', 'A-Level', 'DSE'].map((exam) => (
                              <option key={exam} value={exam}>{exam}</option>
                            ))}
                          </select>
                        </div>

                        {goalData.exam_type === 'IB' && (
                          <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                            <label className="block text-sm font-medium text-gray-300 mb-3">
                              IB Level
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {['IB SL', 'IB HL'].map((level) => (
                                <button
                                  key={level}
                                  type="button"
                                  onClick={() => setGoalData(prev => ({ ...prev, exam_level: level }))}
                                  className={`p-2 rounded-md text-sm font-medium transition-all ${
                                    goalData.exam_level === level
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                  }`}
                                >
                                  {level}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {goalData.exam_type && (goalData.exam_type !== 'IB' || goalData.exam_level) && (
                          <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                            <label className="block text-sm font-medium text-gray-300 mb-3">
                              Year/Phase
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {(() => {
                                if (goalData.exam_type.includes('IGCSE')) return ['Y10', 'Y11', 'Intensive'];
                                if (goalData.exam_type === 'IB') return ['Year 1', 'Year 2', 'Intensive'];
                                if (goalData.exam_type.includes('A-Level')) return ['AS', 'A2', 'Retake', 'Intensive'];
                                if (goalData.exam_type.includes('DSE')) return ['F4', 'F5', 'F6', 'Intensive'];
                                return [];
                              })().map((phase) => (
                                <button
                                  key={phase}
                                  type="button"
                                  onClick={() => setGoalData(prev => ({ ...prev, exam_year_phase: phase }))}
                                  className={`p-2 rounded-md text-sm font-medium transition-all ${
                                    goalData.exam_year_phase === phase
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                  }`}
                                >
                                  {phase}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Registration Form Card */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-6 py-3 border-b border-gray-700">
              <h3 className="font-semibold text-base flex items-center">
                <Mail size={16} className="mr-2" />
                How can we reach you?
              </h3>
            </div>
            
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Full Name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  error={fieldErrors.name}
                  required
                />
                
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  error={fieldErrors.email}
                  required
                />
                
                <Input
                  label="Phone Number (for SMS/WhatsApp)"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+852 1234 5678"
                  helperText="Include country code (e.g., +852 for Hong Kong)"
                  error={fieldErrors.phone}
                />

                <div className="pt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={isLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Submit Availability
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      <HelpButton />
      <Footer />
    </div>
  );
};

export default StartRegisterPage;